/**
 * Tiny global UI-overlay priority bus.
 *
 * Higher-priority UI layers (language dropdown, project inquiry panel,
 * mobile menu, etc.) call `setOverlay(key, open)` to register their open state.
 * The Selection / Lighting Cockpit trigger subscribes via `useTopOverlay()`
 * and decides how to duck/hide.
 *
 * Priority order (higher index = wins):
 *   0 normal page content
 *   1 language switcher dropdown / nav dropdowns
 *   2 mobile menu
 *   3 project inquiry panel / contact / assistant
 *   4 modals / lightboxes (reserved)
 */

import { useEffect, useState } from "react";

export type OverlayKey =
  | "language-dropdown"
  | "nav-dropdown"
  | "mobile-menu"
  | "hud"
  | "inquiry-panel"
  | "modal";

export type SuppressionLevel = "none" | "duck" | "hide";

const PRIORITY: Record<OverlayKey, number> = {
  "language-dropdown": 1,
  "nav-dropdown": 1,
  "mobile-menu": 2,
  hud: 3,
  "inquiry-panel": 3,
  modal: 4,
};

const LEVEL: Record<OverlayKey, SuppressionLevel> = {
  "language-dropdown": "duck",
  "nav-dropdown": "duck",
  "mobile-menu": "hide",
  hud: "hide",
  "inquiry-panel": "hide",
  modal: "hide",
};

const EVENT = "fp:ui-overlay";
const open = new Set<OverlayKey>();

const BODY_CLASS: Record<OverlayKey, string> = {
  "language-dropdown": "language-menu-open",
  "nav-dropdown": "language-menu-open",
  "mobile-menu": "mobile-menu-open",
  hud: "hud-open",
  "inquiry-panel": "project-assistant-open",
  modal: "modal-open",
};

function syncBodyClasses() {
  if (typeof document === "undefined") return;
  const classes = Array.from(new Set(Object.values(BODY_CLASS)));
  classes.forEach((className) => {
    const active = Array.from(open).some((key) => BODY_CLASS[key] === className);
    document.body.classList.toggle(className, active);
  });
}

function emit() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(EVENT));
}

export function setOverlay(key: OverlayKey, isOpen: boolean) {
  const had = open.has(key);
  if (isOpen) open.add(key);
  else open.delete(key);
  syncBodyClasses();
  if (had !== isOpen) emit();
}

function topLevel(): SuppressionLevel {
  let bestKey: OverlayKey | null = null;
  let bestPrio = -1;
  open.forEach((k) => {
    const p = PRIORITY[k] ?? 0;
    if (p > bestPrio) {
      bestPrio = p;
      bestKey = k;
    }
  });
  return bestKey ? LEVEL[bestKey] : "none";
}

/** React hook returning current top-overlay suppression level. */
export function useTopOverlay(): SuppressionLevel {
  const [level, setLevel] = useState<SuppressionLevel>(() => topLevel());
  useEffect(() => {
    const onChange = () => setLevel(topLevel());
    window.addEventListener(EVENT, onChange);
    return () => window.removeEventListener(EVENT, onChange);
  }, []);
  return level;
}
